import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Users, DollarSign, Calendar, TrendingUp, Star, Clock, CheckCircle, AlertCircle, Mail, Phone, MapPin, X, LayoutDashboard } from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetProviderDashboardSummary, getGetProviderDashboardSummaryQueryKey, useGetMyInvitations, useAcceptInvitation, useGetProviderAppointments } from "@/lib/api-client-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from "recharts";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.4 } })
};

export default function ProviderDashboard() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const { data: apiData, isLoading: isSummaryLoading } = useGetProviderDashboardSummary({ query: { queryKey: getGetProviderDashboardSummaryQueryKey() } });
  const { data: appointmentsData, isLoading: isApptsLoading } = useGetProviderAppointments({ page: 1, limit: 1000 });
  const allAppointments = appointmentsData?.items || [];
  const realAppointments = allAppointments.filter((a: any) => a.status === 'APPROVED').slice(0, 5);
  const pendingRequests = allAppointments.filter((a: any) => a.status === 'PENDING').slice(0, 5);
  const isLoading = isSummaryLoading || isApptsLoading;
  
  // Calculate real metrics from appointments
  const completedAppointments = allAppointments.filter((a: any) => a.status === 'COMPLETED');
  const totalRevenue = completedAppointments.reduce((sum: number, a: any) => sum + (Number(a.amount) || 0), 0);
  
  const today = new Date().toISOString().split('T')[0];
  const todayAppointmentsList = allAppointments.filter((a: any) => a.createdAt && a.createdAt.startsWith(today) && (a.status === 'APPROVED' || a.status === 'COMPLETED'));
  const todayCompleted = completedAppointments.filter((a: any) => a.createdAt && a.createdAt.startsWith(today));
  const dailyRevenue = todayCompleted.reduce((sum: number, a: any) => sum + (Number(a.amount) || 0), 0);
  
  const pendingAppointmentsCount = allAppointments.filter((a: any) => a.status === 'PENDING').length;

  // Calculate real monthly trend for the last 6 months
  const now = new Date();
  const last6Months = Array.from({ length: 6 }).map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return { 
      month: d.toLocaleString('default', { month: 'short' }),
      amount: 0,
      yearMonth: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    };
  });

  completedAppointments.forEach((a: any) => {
    if (!a.createdAt) return;
    const date = new Date(a.createdAt);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const targetMonth = last6Months.find(m => m.yearMonth === yearMonth);
    if (targetMonth) {
      targetMonth.amount += Number(a.amount) || 0;
    }
  });
  const realMonthlyTrend = last6Months.map(({ month, amount }) => ({ month, amount }));

  // Calculate real monthly patients for the last 6 months
  const last6MonthsPatients = Array.from({ length: 6 }).map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return { 
      month: d.toLocaleString('default', { month: 'short' }),
      amount: 0,
      yearMonth: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      uniquePatients: new Set<string>()
    };
  });

  allAppointments.forEach((a: any) => {
    if (!a.createdAt || (a.status !== 'COMPLETED' && a.status !== 'APPROVED')) return;
    const date = new Date(a.createdAt);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const targetMonth = last6MonthsPatients.find(m => m.yearMonth === yearMonth);
    if (targetMonth) {
      if (a.memberId) {
        targetMonth.uniquePatients.add(a.memberId);
      } else {
        targetMonth.amount++;
      }
    }
  });
  
  const realMonthlyPatients = last6MonthsPatients.map(({ month, amount, uniquePatients }) => ({ 
    month, 
    amount: uniquePatients.size > 0 ? uniquePatients.size : amount 
  }));

  const data = apiData?.revenue?.daily ? apiData : {
    activePatients: allAppointments.length || 142,
    pendingAppointments: pendingAppointmentsCount,
    pendingEstimates: 0,
    revenue: {
      daily: dailyRevenue,
      monthly: totalRevenue,
      monthlyTrend: realMonthlyTrend
    },
    todayAppointments: todayAppointmentsList,
    performanceCharts: {
      patientSatisfaction: 4.8,
      appointmentCompletion: 94,
      avgWaitTime: 12,
      monthlyPatients: realMonthlyPatients
    },
    tasks: [
      { id: '1', title: 'Review lab results for M. Chen', priority: 'high', dueDate: 'Today, 5:00 PM', completed: false },
      { id: '2', title: 'Sign GFEs for upcoming procedures', priority: 'medium', dueDate: 'Tomorrow, 12:00 PM', completed: false },
      { id: '3', title: 'Update clinic holiday hours', priority: 'low', dueDate: 'Next Week', completed: false },
    ]
  };
  
  // Use a unique query key per user to prevent cross-user caching issues
  const { data: myInvitations } = useGetMyInvitations(user?.id, { enabled: !!user });
  const acceptMutation = useAcceptInvitation();
  const [dismissBanner, setDismissBanner] = useState(false);
  const queryClient = useQueryClient();

  const handleAcceptInvite = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
      toast({ title: "Success", description: "You have joined the group practice!" });
      queryClient.invalidateQueries();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  return (
    <ProviderLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-6">
        
        {myInvitations?.data?.filter((inv: any) => inv.status === 'PENDING').length > 0 && !dismissBanner && (() => {
          const pendingInv = myInvitations.data.find((inv: any) => inv.status === 'PENDING');
          return (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 rounded-2xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-200/50 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm font-medium text-blue-800 dark:text-blue-200">
                  <span>🎉 You have been invited to join a group practice ({pendingInv.group?.user?.displayName || (pendingInv.group?.user?.firstName ? `${pendingInv.group.user.firstName} ${pendingInv.group.user.lastName}` : "A Group Practice")}).</span>
                  <Button onClick={() => handleAcceptInvite(pendingInv.id)} disabled={acceptMutation.isPending} variant="default" size="sm" className="h-7 px-3 py-0 text-xs rounded-full ml-3 shadow-sm bg-blue-600 hover:bg-blue-700 text-white">
                    Accept
                  </Button>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-600 hover:bg-blue-200/50 rounded-full" onClick={() => setDismissBanner(true)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          );
        })()}

        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
            <LayoutDashboard className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Provider Dashboard</h1>
            <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.displayName || (user?.firstName ? `${user.firstName} ${user.lastName}` : "Dr. Reynolds")}. Here's your practice overview.</p>
          </div>
        </motion.div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Today's Revenue", value: isLoading ? "—" : `$${(data?.revenue?.daily ?? 0).toLocaleString()}`, icon: DollarSign, color: "text-green-600", bg: "bg-green-50 dark:bg-green-900/20", trend: "+12%", href: "/provider/reports" },
            { label: "Monthly Revenue", value: isLoading ? "—" : `$${(data?.revenue?.monthly ?? 0).toLocaleString()}`, icon: TrendingUp, color: "text-primary", bg: "bg-primary/10", trend: "+8%", href: "/provider/reports" },
            { label: "Today's Patients", value: isLoading ? "—" : String(data?.todayAppointments?.length ?? 0), icon: Calendar, color: "text-secondary", bg: "bg-secondary/10", trend: "+2", href: "/provider/appointments" },
            { label: "Pending Requests", value: isLoading ? "—" : String(data?.pendingAppointments ?? 0), icon: AlertCircle, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-900/20", trend: "", href: "/provider/appointments" },
          ].map((stat, i) => (
            <motion.div key={stat.label} custom={i + 1} variants={fadeUp} initial="hidden" animate="visible">
              <div 
                onClick={() => setLocation(stat.href)}
                className="bg-card rounded-2xl border border-border p-5 shadow-xs hover:shadow-md hover:border-primary/40 cursor-pointer transition-all" 
                data-testid={`stat-${stat.label}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  {stat.trend && <span className="text-xs font-medium text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">{stat.trend}</span>}
                </div>
                {isLoading ? <Skeleton className="h-7 w-20 mb-1" /> : <p className="text-2xl font-bold">{stat.value}</p>}
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible" className="lg:col-span-2">
            <div 
              onClick={() => setLocation('/provider/reports')}
              className="bg-card rounded-2xl border border-border p-6 shadow-xs hover:shadow-md hover:border-primary/40 cursor-pointer transition-all"
            >
              <h2 className="font-semibold mb-5">Monthly Revenue</h2>
              {isLoading ? <Skeleton className="h-48 w-full" /> : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data?.revenue?.monthlyTrend ?? []}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => [`$${v.toLocaleString()}`, "Revenue"]} />
                    <Area type="monotone" dataKey="amount" stroke="#2563EB" strokeWidth={2} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>

          {/* Performance */}
          <motion.div custom={6} variants={fadeUp} initial="hidden" animate="visible">
            <div 
              onClick={() => setLocation('/provider/reports')}
              className="bg-card rounded-2xl border border-border p-5 shadow-xs h-full hover:shadow-md hover:border-primary/40 cursor-pointer transition-all"
            >
              <h2 className="font-semibold mb-4">Performance</h2>
              {isLoading ? <div className="space-y-3">{Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-10 w-full"/>)}</div> : (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-muted-foreground">Patient Satisfaction</span>
                      <span className="font-semibold flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        {data?.performanceCharts?.patientSatisfaction}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{width:`${(data?.performanceCharts?.patientSatisfaction ?? 0) / 5 * 100}%`}} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-muted-foreground">Appointment Completion</span>
                      <span className="font-semibold">{data?.performanceCharts?.appointmentCompletion}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{width:`${data?.performanceCharts?.appointmentCompletion}%`}} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-muted-foreground">Avg Wait Time</span>
                      <span className="font-semibold">{data?.performanceCharts?.avgWaitTime} min</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{width:`${Math.min(100, (data?.performanceCharts?.avgWaitTime ?? 0) / 30 * 100)}%`}} />
                    </div>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <ResponsiveContainer width="100%" height={80}>
                      <BarChart data={data?.performanceCharts?.monthlyPatients ?? []} barSize={8}>
                        <Bar dataKey="amount" fill="#2563EB" radius={[2,2,0,0]} />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v: number) => [v, "Patients"]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-center text-muted-foreground">Monthly Patients</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scheduled Appointments */}
          <motion.div custom={7} variants={fadeUp} initial="hidden" animate="visible">
            <div className="bg-card rounded-[12px] border border-border shadow-sm">
              <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                <h2 className="font-semibold text-lg">Scheduled Appointments</h2>
                <Link href="/provider/appointments"><Button variant="ghost" size="sm" className="text-primary text-xs">View All</Button></Link>
              </div>
              <div className="flex flex-col">
                {isLoading ? Array.from({length:3}).map((_,i) => (
                  <div key={i} className="p-6 border-b border-border last:border-0 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10 rounded-xl" />
                      <div className="flex-1"><Skeleton className="h-4 w-32 mb-1.5" /><Skeleton className="h-3 w-40" /></div>
                      <Skeleton className="h-6 w-20 rounded-lg" />
                    </div>
                    <Skeleton className="h-10 w-full rounded-lg" />
                  </div>
                )) : realAppointments.length === 0 ? (
                  <div className="px-5 py-10 text-center text-muted-foreground text-sm border-b border-border last:border-0">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />No appointments
                  </div>
                ) : realAppointments.map((appt: any) => {
                  const patientName = appt.member?.user?.displayName || `${appt.member?.user?.firstName || ''} ${appt.member?.user?.lastName || ''}`.trim() || 'Patient';
                  const address = appt.member?.user?.addresses?.[0];
                  const addressStr = address ? [address.city, address.state, address.zipCode].filter(Boolean).join(', ') : '';

                  return (
                  <div 
                    key={appt.id} 
                    onClick={() => setLocation('/provider/appointments')}
                    className="px-6 py-5 border-b border-border last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all flex flex-col gap-3 cursor-pointer" 
                    data-testid={`appt-${appt.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#f0f7ff] dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 border border-blue-100/50 dark:border-blue-800/50">
                          <span className="text-lg font-bold text-[#2563eb] dark:text-blue-400">{patientName.charAt(0)}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white text-[15px]">{patientName}</h3>
                          <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-0.5">
                            {appt.bundle?.name || 'General Visit'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-200/60 dark:border-slate-700">
                        <Clock className="w-3.5 h-3.5 text-blue-500" />
                        {format(new Date(appt.createdAt || Date.now()), 'MMM d, h:mm a')}
                      </div>
                    </div>
                    
                    {(appt.member?.user?.email || appt.member?.user?.phone || addressStr) && (
                      <>
                        <div className="flex flex-col gap-1.5 text-[12px] text-slate-500 dark:text-slate-400 mt-0">
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
                            {appt.member?.user?.email && (
                              <div className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                                <span className="truncate">{appt.member.user.email}</span>
                              </div>
                            )}
                            {appt.member?.user?.phone && (
                              <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                                <span className="truncate">{appt.member.user.phone}</span>
                              </div>
                            )}
                          </div>
                          {addressStr && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                              <span className="truncate">{addressStr}</span>
                            </div>
                          )}
                        </div>
                        <div className="h-px bg-slate-100 dark:bg-slate-800/60 w-full mt-2.5 mb-0" />
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Pending Requests */}
          <motion.div custom={8} variants={fadeUp} initial="hidden" animate="visible">
            <div className="bg-card rounded-[12px] border border-border shadow-sm">
              <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                <h2 className="font-semibold text-lg">Pending Requests</h2>
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">{pendingRequests.length} New</Badge>
              </div>
              <div className="flex flex-col">
                {isLoading ? Array.from({length:2}).map((_,i) => (
                  <div key={i} className="p-6 border-b border-border last:border-0 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10 rounded-xl" />
                      <div className="flex-1"><Skeleton className="h-4 w-32 mb-1.5" /><Skeleton className="h-3 w-40" /></div>
                      <Skeleton className="h-6 w-20 rounded-lg" />
                    </div>
                  </div>
                )) : pendingRequests.length === 0 ? (
                  <div className="px-5 py-10 text-center text-muted-foreground text-sm border-b border-border last:border-0">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />No pending requests
                  </div>
                ) : pendingRequests.map((appt: any) => {
                  const patientName = appt.member?.user?.displayName || `${appt.member?.user?.firstName || ''} ${appt.member?.user?.lastName || ''}`.trim() || 'Patient';
                  const address = appt.member?.user?.addresses?.[0];
                  const addressStr = address ? [address.city, address.state, address.zipCode].filter(Boolean).join(', ') : '';

                  return (
                  <div 
                    key={appt.id} 
                    onClick={() => setLocation('/provider/appointments')}
                    className="px-6 py-5 border-b border-border last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-all flex flex-col gap-4 cursor-pointer" 
                    data-testid={`pending-${appt.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0 border border-amber-200/50 dark:border-amber-800/50">
                          <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{patientName.charAt(0)}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white text-[15px]">{patientName}</h3>
                          <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-0.5">
                            Requested: {format(new Date(appt.createdAt || Date.now()), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {(appt.member?.user?.email || appt.member?.user?.phone || addressStr) && (
                      <>
                        <div className="flex flex-col gap-1.5 text-[12px] text-slate-500 dark:text-slate-400 mt-0">
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
                            {appt.member?.user?.email && (
                              <div className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                                <span className="truncate">{appt.member.user.email}</span>
                              </div>
                            )}
                            {appt.member?.user?.phone && (
                              <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                                <span className="truncate">{appt.member.user.phone}</span>
                              </div>
                            )}
                          </div>
                          {addressStr && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                              <span className="truncate">{addressStr}</span>
                            </div>
                          )}
                        </div>
                        <div className="h-px bg-slate-100 dark:bg-slate-800/60 w-full mb-0" />
                      </>
                    )}

                    <div className="flex gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                      <Button className="flex-1 bg-primary text-white hover:bg-primary/90">Accept</Button>
                      <Button variant="outline" className="flex-1 text-destructive hover:bg-destructive hover:text-white border-destructive/20">Decline</Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </ProviderLayout>
  );
}
